import { useCallback, useSyncExternalStore } from "react";

// The screens' language, English or Arabic, for screens that offer both (the sign-in, the websites list, AFAQ's
// dashboard). Each person's choice is kept in this browser; the first time it follows the browser's language.

export type UiLang = "en" | "ar";
const KEY = "hub-lang";

function first(): UiLang {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "en" || saved === "ar") return saved;
  } catch {
    // no storage (private window): fall through
  }
  return typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("ar") ? "ar" : "en";
}

let current: UiLang = first();
const listeners = new Set<() => void>();

/** The language now, outside React (e.g. for a request's Accept-Language). */
export const getUiLang = () => current;

export function setUiLang(lang: UiLang) {
  current = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // kept for this visit only
  }
  for (const listen of listeners) listen();
}

const subscribe = (listen: () => void) => {
  listeners.add(listen);
  return () => listeners.delete(listen);
};

/** The language, a way to pick the text for it (`L("Save", "حفظ")`), and its writing direction. */
export function useUiLang() {
  const lang = useSyncExternalStore(subscribe, () => current);
  const L = useCallback(<T,>(en: T, ar: T): T => (lang === "ar" ? ar : en), [lang]);
  return { lang, L, dir: lang === "ar" ? ("rtl" as const) : ("ltr" as const), setLang: setUiLang };
}
